<template>
  <div>
    <page-header
      :page="pageConfig"
      :actions="pageActionsConfig"
      :has-return="true"
    />
    <progress-filter
      :academic-years="academicYears"
      :default-value="lastAcademicYear"
      @on-change-academic-year="onChangeAcademicYearHandler"
    />
    <section-title
      class="mb-3 pl-1 ml-1 section-title__extended"
      icon="fas fa-clipboard-list-check"
      title="Resumen de Información General"
      title-class="font-16"
    />
    <div class="row">
      <div class="col col-sm col-12 ml-2">
        <general-info-summary
          title="Adicional"
          :total-count="summaryAdditionalEvaluation.totalCount"
          :pending-count="summaryAdditionalEvaluation.pendingCount"
          :completed-count="summaryAdditionalEvaluation.completedPercentage"
        />
      </div>
      <div class="col col-sm col-12 ml-2 mt-2 mt-sm-2 mt-md-0">
        <general-info-summary
          title="Reevaluación"
          :total-count="summaryReEvaluation.totalCount"
          :pending-count="summaryReEvaluation.pendingCount"
          :completed-count="summaryReEvaluation.completedPercentage"
        />
      </div>
      <div class="col col-sm col-12 ml-2 mt-2 mt-sm-2 mt-md-0">
        <general-info-summary
          title="Evaluación Trianual"
          :total-count="summaryTrianual.totalCount"
          :pending-count="summaryTrianual.pendingCount"
          :completed-count="summaryTrianual.completedPercentage"
        />
      </div>
    </div>
    <div class="ml-2 mt-3">
      <summary-collapse
        :is-opened="true"
        :action-summary="actionSummary"
        title="Detalle de Resumen Actual"
      >
        <progress-info-summary :items="currentProgressSummary" />
      </summary-collapse>
    </div>
    <div class="mt-4 ml-2">
      <section-title has-line title="Historial de Informes de Progreso" />
      <progress-info-history
        :items="progressHistory"
        :action-items="actionItems"
        @on-refresh="onRefreshHandler"
        @on-close-ip="onCloseIpHandler"
        @on-update-ip="onUpdateIpHandler"
        @on-open-detail="onOpenDetailHandler"
      />
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import SectionTitle from 'SectionTitle';
import {
  GeneralInfoSummary,
  ProgressFilter,
  ProgressInfoHistory,
  ProgressInfoSummary,
  SummaryCollapse,
} from '../shared/components';
import Service from '@/services/api/progress.api';
import DocumentServices from '@/modules/report/document';
import stipulationType from '@/utils/constants/stipulationType.js';

export default {
  components: {
    PageHeader,
    ProgressFilter,
    SectionTitle,
    GeneralInfoSummary,
    SummaryCollapse,
    ProgressInfoSummary,
    ProgressInfoHistory,
  },
  data: () => ({
    academicYears: [],
    selectedAcademicYear: null,
    summaryAdditionalEvaluation: {},
    summaryReEvaluation: {},
    summaryTrianual: {},
    progressHistory: [],
    currentProgressSummary: [],
  }),
  computed: {
    pageConfig() {
      return {
        title: 'Informes de Progreso: Estipulación 49',
        icon: 'fas fa-calculator',
        routes: ['Inicio', 'Informes de Progreso', 'Estipulación 49'],
      };
    },
    pageActionsConfig() {
      return [
        {
          text: 'Actualizar IP',
          icon: 'fas fa-sync-alt pr-2',
          variant: 'success',
          visible: true,
          click: () => this.onUpdateProgressReportHandler(),
        },
      ];
    },
    actionSummary() {
      return () => {
        return [
          {
            label: '',
            icon: 'fas fa-print text-green',
            class: 'pr-1',
            visible: true,
            click: () => this.onDownloadSummaryHandler(),
          },
        ];
      };
    },
    actionItems() {
      return (item) => {
        return [
          {
            label: '',
            icon: 'fas fa-print text-green',
            class: 'pr-1',
            visible: true,
            click: () => this.onDownloadHandler(item),
          },
        ];
      };
    },
    stipulationType() {
      return stipulationType.ST49;
    },
    lastAcademicYear() {
      if (!this.academicYears?.length) {
        return null;
      }
      return this.academicYears[0];
    },
    isLastAcademicYearSelected() {
      if (!this.academicYears?.length) {
        return false;
      }
      return this.lastAcademicYear?.id === this.selectedAcademicYear?.id;
    },
    isLastIpClosed() {
      if (!this.progressHistory.length) return true;
      return this.progressHistory[0].isClosed;
    },
  },
  async mounted() {
    await this.getInit();
  },
  methods: {
    async getInit() {
      await this.getAvailableAcademicYears();
      if (this.academicYears?.length) {
        this.selectedAcademicYear = this.lastAcademicYear;
        await this.getDetail();
      }
    },
    async getAvailableAcademicYears() {
      this.academicYears = (
        await Service.getAcademicYears(this.stipulationType)
      ).data;
    },
    async getDetail() {
      const response = (
        await Service.getDetail({
          stipulationType: this.stipulationType,
          academicYearId: this.selectedAcademicYear.id,
        })
      ).data;
      if (response) {
        this.summaryAdditionalEvaluation = response?.summaries[0];
        this.summaryReEvaluation = response?.summaries[1];
        this.summaryTrianual = response?.summaries[2];
        this.currentProgressSummary = response.currentSummaryDetail;
        this.progressHistory = response.progressHistory;
      }
    },
    onChangeAcademicYearHandler(academicYear) {
      this.selectedAcademicYear = academicYear;
      this.getDetail();
    },
    async onRefreshHandler() {
      await this.getDetail();
    },
    onCloseIpHandler({ summaryId }) {
      if (summaryId) {
        this.ShowCancelModal(
          'Alerta',
          '¿Está seguro que desea congelar los datos y cerrar un IP?',
          'Sí',
          'No'
        ).then(async (res) => {
          if (res) {
            const result = (
              await Service.closeIp(this.stipulationType, summaryId)
            ).data;
            if (result) {
              this.ShowToast(
                'Proceso Exitoso',
                'El IP ha sido cerrado exitosamente.',
                'success'
              );
              this.onRefreshHandler();
            }
          }
        });
      }
    },
    onUpdateIpHandler() {
      Service.updateIp(
        this.stipulationType,
        this.selectedAcademicYear.id,
        true
      ).then((res) => {
        if (res.data) {
          this.ShowToast(
            'Proceso Exitoso',
            `El IP ha sido actualizado exitosamente.`,
            'success'
          );
          this.onRefreshHandler();
        } else {
          this.ShowToast(
            'Alerta',
            'No cuenta con datos para actualizar en sistema.',
            'error'
          );
        }
      });
    },
    onUpdateProgressReportHandler() {
      if (!this.isLastIpClosed) {
        this.ShowToast(
          'Alerta',
          'Para crear una nueva actualización es necesario cerrar el último IP.',
          'error'
        );
      } else {
        this.ShowCancelModal(
          'Alerta',
          '¿Está seguro que desea actualizar los datos y generar un IP?',
          'Sí',
          'No'
        ).then((res) => {
          if (res) {
            Service.updateIp(
              this.stipulationType,
              this.selectedAcademicYear.id,
              false
            ).then((res) => {
              if (res.data) {
                this.ShowToast(
                  'Proceso Exitoso',
                  'El IP ha sido actualizado exitosamente.',
                  'success'
                );
                this.onRefreshHandler();
              } else {
                this.ShowToast(
                  'Alerta',
                  'No es posible generar un IP, ya que no cuenta con datos para actualizar en sistema.',
                  'error'
                );
              }
            });
          }
        });
      }
    },
    async onDownloadHandler(item) {
      await Service.downloadIpStipulation({
        stipulationType: this.stipulationType,
        ipSequence: item.ipSequence,
        academicYearId: item.academicYearId,
      })
        .then((res) => DocumentServices.Download(res.data))
        .catch((err) => {
          this.ShowToast('', err.response?.data?.message, 'error');
        });
    },
    async onDownloadSummaryHandler() {
      if (this.selectedAcademicYear) {
        var academicYearId = this.selectedAcademicYear.id;
        this.onDownloadHandler({ academicYearId, ipSequence: 0 });
      }
    },
    async onOpenDetailHandler(index) {
      this.progressHistory = await Promise.all(
        this.progressHistory.map(async (item, itemIndex) => {
          if (itemIndex === index) {
            item.isOpen = index === itemIndex ? !item.isOpen : false;
            if (item.isOpen && !item.summary) {
              item.summary = (
                await Service.getIpHistory(
                  this.stipulationType,
                  this.selectedAcademicYear.id,
                  item.ipSequence
                )
              ).data;
            }
          }
          return item;
        })
      );
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/views/progress/shared/scss/common.scss';
</style>
